/**
 * Organization Chart — visual hierarchy by department & seniority.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, Building2, Crown, Loader, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_COLOR = [
  'from-amber-500 to-amber-700',     // 0 Executive
  'from-purple-500 to-purple-700',   // 1 Department Head
  'from-blue-500 to-blue-700',       // 2 Senior Manager
  'from-blue-400 to-blue-600',       // 3 Manager
  'from-emerald-500 to-emerald-700', // 4 Supervisor
  'from-teal-500 to-teal-700',       // 5 Senior
  'from-slate-500 to-slate-700',     // 6 Executive Level
  'from-gray-400 to-gray-600',       // 7 Staff
  'from-gray-300 to-gray-500',       // 8 Junior
];

const PersonCard = ({ person }) => {
  const colorClass = LEVEL_COLOR[person.level] || LEVEL_COLOR[7];
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3 hover:shadow-lg hover:border-red-200 transition-all" data-testid={`person-${person.id}`}>
      <div className="flex items-start gap-3">
        <div className={`shrink-0 w-12 h-12 bg-gradient-to-br ${colorClass} rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-md`}>
          {person.initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-gray-800 truncate">{person.name}</p>
          <p className="text-xs text-gray-500 truncate">{person.position}</p>
          <span className="inline-block mt-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600 rounded">
            {person.level_label}
          </span>
        </div>
      </div>
      {(person.email || person.phone) && (
        <div className="mt-2 pt-2 border-t border-gray-100 space-y-0.5">
          {person.email && (
            <p className="text-[11px] text-gray-500 flex items-center gap-1.5 truncate">
              <Mail className="w-3 h-3 shrink-0" /> {person.email}
            </p>
          )}
          {person.phone && (
            <p className="text-[11px] text-gray-500 flex items-center gap-1.5">
              <Phone className="w-3 h-3 shrink-0" /> {person.phone}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const OrgChartPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/hr/org-chart`);
      setData(data);
    } catch {
      toast.error('Gagal memuat carta organisasi');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-5" data-testid="org-chart-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Building2 className="w-6 h-6 text-red-700" /> Carta Organisasi
        </h1>
        <p className="text-gray-500 text-xs sm:text-sm">Hierarki staff Underads disusun ikut department dan kanan-junior.</p>
      </div>

      {/* Summary stats */}
      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border-l-4 border-l-red-500 border border-gray-100 p-4">
            <p className="text-xs text-gray-500">Total Staff Aktif</p>
            <p className="text-2xl font-bold text-red-700">{data.total_staff}</p>
          </div>
          <div className="bg-white rounded-xl border-l-4 border-l-blue-500 border border-gray-100 p-4">
            <p className="text-xs text-gray-500">Department</p>
            <p className="text-2xl font-bold text-blue-700">{data.department_count}</p>
          </div>
          <div className="bg-white rounded-xl border-l-4 border-l-amber-500 border border-gray-100 p-4">
            <p className="text-xs text-gray-500">Senior (Manager+)</p>
            <p className="text-2xl font-bold text-amber-700">
              {Object.entries(data.level_distribution || {})
                .filter(([k]) => /Executive|Head|Manager|Senior Manager/.test(k))
                .reduce((s, [, v]) => s + v, 0)}
            </p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-2xl p-12 flex justify-center"><Loader className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : !data?.departments?.length ? (
        <div className="bg-white rounded-2xl p-12 text-center text-gray-400">Tiada data staff.</div>
      ) : (
        <div className="space-y-6">
          {data.departments.map((dept) => (
            <div key={dept.department} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              {/* Department header */}
              <div className="bg-gradient-to-r from-red-700 to-red-900 text-white p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Crown className="w-5 h-5 text-amber-300" />
                  <div>
                    <h2 className="text-base font-bold">{dept.department}</h2>
                    <p className="text-xs text-red-100/80">{dept.head_count} ahli</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(dept.level_counts).map(([lvl, cnt]) => (
                    <span key={lvl} className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-white/20 rounded-full">
                      {lvl}: {cnt}
                    </span>
                  ))}
                </div>
              </div>
              {/* Members grid */}
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {dept.members.map((m) => <PersonCard key={m.id} person={m} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrgChartPage;
